// Invitación de amigos — SERVER-CIEGO. El "premio" es IKOGNIT; no hay recompensas ni backend nuevo.
//
// Modelo (idéntico patrón server-ciego que SharePage `/s/` + fragmento): la invitación es un enlace
//   https://app.ikognit.com/i#<carga>
// donde <carga> es un JSON base64url que viaja en el FRAGMENTO `#` → el navegador NUNCA lo manda al
// servidor. La carga lleva la identidad del invitador (ikognit_id + clave pública + nick), el CONTEXTO
// (chat/pod/…) y un TOKEN de un solo uso. El invitado, tras entrar, añade al invitador como contacto
// (con la clave pública que viene en la carga, sin lookup) y le devuelve un HANDSHAKE sellado por el
// chat normal que ECOA el token; el invitador casa el token con el que guardó (friend_invites) y
// auto-confirma al amigo. El servidor solo ve un registro más y un blob sellado: no aprende quién
// invitó a quién.

// IMPORTANTE: este módulo NO depende de libsodium. main.tsx parsea la invitación en el ARRANQUE,
// antes de `sodium.ready`; si usáramos crypto.b64decode (sodium.from_base64) lanzaría y la invitación
// se perdería en silencio. Por eso el base64/base64url va en JS puro (atob/btoa), disponible ya.

export type InviteContext = 'chat' | 'pod' | 'group' | 'domain';
export const INVITE_VERSION = 1;
// Origen sobre el que se construyen los enlaces de invitación: el de la propia
// web en la que corre (así el enlace generado en desarrollo apunta a desarrollo).
export const APP_ORIGIN = (typeof window !== 'undefined' && window.location?.origin)
  ? window.location.origin
  : 'https://app.ikognit.com';

/** Carga de la invitación (va en el fragmento del enlace). */
export interface InvitePayload {
  v: number;             // versión de formato
  from: string;          // ikognit_id del invitador
  pk: string;            // clave pública (identidad) del invitador, base64
  nick?: string;         // nick visible del invitador (para el "X te invita")
  ctx: InviteContext;    // por qué te invita
  tok: string;           // token de un solo uso (base64url) — lo ecoa el handshake
  exp: number;           // caducidad unix (s)
  // Opcional según contexto (p.ej. la invitación al pod ya firmada). Se rellena aparte.
  extra?: string;
}

/** Handshake que el INVITADO devuelve al invitador por el chat (mensaje de texto con prefijo). */
export interface FriendHandshake {
  from: string;          // ikognit_id del invitado
  pk: string;            // clave pública del invitado, base64
  nick?: string;
  tok: string;           // token ecoado (el mismo de la invitación)
}

const HS_PREFIX = 'IKOGNITFRIEND1.';
const enc = new TextEncoder();
const dec = new TextDecoder();

// base64 estándar en JS puro (sin sodium): bytes ↔ binary-string ↔ atob/btoa.
function bytesToB64(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}
function b64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
/** Longitud en bytes de un base64 ESTÁNDAR (para el sanity de la clave pública), sin sodium. */
function b64Len(b64: string): number {
  try { return atob(b64).length; } catch { return -1; }
}
function b64urlEncodeBytes(bytes: Uint8Array): string {
  return bytesToB64(bytes).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
function b64urlDecodeBytes(s: string): Uint8Array {
  let b64 = s.replace(/-/g, '+').replace(/_/g, '/');
  while (b64.length % 4) b64 += '=';   // restaura el padding que b64url quita (atob lo exige)
  return b64ToBytes(b64);
}
function b64urlEncodeJson(obj: unknown): string {
  return b64urlEncodeBytes(enc.encode(JSON.stringify(obj)));
}
function b64urlDecodeJson<T>(s: string): T {
  return JSON.parse(dec.decode(b64urlDecodeBytes(s))) as T;
}

/** Token aleatorio de un solo uso (16 bytes → base64url). */
export function newInviteToken(): string {
  const b = new Uint8Array(16);
  globalThis.crypto.getRandomValues(b);
  return b64urlEncodeBytes(b);
}

/** TTL de una invitación (la OFERTA). 30 días. */
export const INVITE_TTL_SECONDS = 30 * 24 * 3600;

/** Construye la carga de invitación desde la sesión del invitador. */
export function buildInvitePayload(
  session: { ikognitId: string; publicKey: Uint8Array; nick?: string },
  ctx: InviteContext = 'chat',
  token: string = newInviteToken(),
  extra?: string,
): { payload: InvitePayload; encoded: string; token: string } {
  const payload: InvitePayload = {
    v: INVITE_VERSION,
    from: session.ikognitId,
    pk: bytesToB64(session.publicKey),
    nick: session.nick,
    ctx,
    tok: token,
    exp: Math.floor(Date.now() / 1000) + INVITE_TTL_SECONDS,
    ...(extra ? { extra } : {}),
  };
  return { payload, encoded: b64urlEncodeJson(payload), token };
}

/** Enlace completo de invitación. La carga va en el FRAGMENTO (server-ciego). */
export function buildInviteLink(encoded: string): string {
  return `${APP_ORIGIN}/i#${encoded}`;
}

/** Lee la invitación del fragmento de la URL actual (o de una cadena). null si no hay o es inválida. */
export function parseInviteFromHash(hash: string = window.location.hash): InvitePayload | null {
  try {
    const raw = (hash || '').replace(/^#/, '').trim();
    if (!raw) return null;
    const p = b64urlDecodeJson<InvitePayload>(raw);
    if (!p || typeof p.from !== 'string' || typeof p.pk !== 'string' || typeof p.tok !== 'string') return null;
    if (!p.exp || p.exp <= Math.floor(Date.now() / 1000)) return null;   // caducada
    // sanity de la clave pública (32 bytes), sin sodium
    if (b64Len(p.pk) !== 32) return null;
    return p;
  } catch {
    return null;
  }
}

/** ¿Estamos en la ruta de invitación (`/i`)? */
export function isInvitePath(pathname: string = window.location.pathname): boolean {
  return pathname === '/i' || pathname.startsWith('/i/');
}

// ── Handshake que el invitado devuelve al invitador (mensaje de texto por el chat) ──

export function encodeFriendHandshake(hs: FriendHandshake): string {
  return HS_PREFIX + b64urlEncodeJson(hs);
}
export function decodeFriendHandshake(text: string): FriendHandshake | null {
  if (!text || !text.startsWith(HS_PREFIX)) return null;
  try {
    const hs = b64urlDecodeJson<FriendHandshake>(text.slice(HS_PREFIX.length));
    if (!hs || typeof hs.from !== 'string' || typeof hs.pk !== 'string' || typeof hs.tok !== 'string') return null;
    if (b64Len(hs.pk) !== 32) return null;
    return hs;
  } catch {
    return null;
  }
}
export function isFriendHandshake(text: string): boolean {
  return !!text && text.startsWith(HS_PREFIX);
}

// ── Compartir el enlace: hoja de compartir NATIVA (iOS/Android) → Web Share API → portapapeles ──

export interface ShareResult { shared: boolean; method: 'native' | 'web' | 'clipboard' | 'none' }

export async function shareInviteLink(link: string, inviterNick?: string): Promise<ShareResult> {
  const title = 'Te invito a IKOGNIT';
  const text = `${inviterNick ? inviterNick + ' te invita' : 'Te invito'} a IKOGNIT — mensajería cifrada de extremo a extremo, con servidor ciego. Ábrelo aquí:`;
  // 1) Nativo (Capacitor) → UIActivityViewController / ACTION_SEND. Import PEREZOSO para que este
  //    módulo sea seguro fuera del navegador (main.tsx/tests lo importan solo para parsear).
  try {
    const { Capacitor } = await import('@capacitor/core');
    if (Capacitor.isNativePlatform()) {
      const { Share } = await import('@capacitor/share');
      await Share.share({ title, text, url: link, dialogTitle: title });
      return { shared: true, method: 'native' };
    }
  } catch { /* cae a web/clipboard */ }
  // 2) Web Share API (móvil web / navegadores compatibles)
  try {
    const nav = navigator as Navigator & { share?: (d: unknown) => Promise<void> };
    if (typeof nav.share === 'function') {
      await nav.share({ title, text, url: link });
      return { shared: true, method: 'web' };
    }
  } catch (e) {
    if ((e as Error)?.name === 'AbortError') return { shared: false, method: 'web' };   // el usuario cerró la hoja
  }
  // 3) Portapapeles
  try { await navigator.clipboard.writeText(link); return { shared: true, method: 'clipboard' }; } catch { /* */ }
  return { shared: false, method: 'none' };
}

// ── Invite PENDIENTE (sobrevive al registro/login del invitado; localStorage con TTL, se limpia al usarse) ──

const PENDING_KEY = 'ikognit_pending_invite';

export function stashPendingInvite(p: InvitePayload): void {
  try { localStorage.setItem(PENDING_KEY, JSON.stringify({ p, at: Date.now() })); } catch { /* */ }
}
export function readPendingInvite(): InvitePayload | null {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const { p, at } = JSON.parse(raw) as { p: InvitePayload; at: number };
    if (!p || Date.now() - at > 7 * 24 * 3600 * 1000) { clearPendingInvite(); return null; }
    if (p.exp && p.exp <= Math.floor(Date.now() / 1000)) { clearPendingInvite(); return null; }
    return p;
  } catch { return null; }
}
export function clearPendingInvite(): void {
  try { localStorage.removeItem(PENDING_KEY); } catch { /* */ }
}
